import API from 'services/api';
import { CAMPAIGNS_LIMIT } from './consts';

const api = new API();

// Unwrap the shared `{status, value}` envelope: non-zero status rejects with the error payload.
const fetchValue = (url, options = {}) =>
    new Promise((resolve, reject) => {
        api.getJson(url, { credentials: 'include', ...options })
            .then(response => {
                if (response.status !== 0) {
                    return reject(response.value);
                }
                return resolve(response.value);
            })
            .catch(reject);
    });

// The attribution-window options come from get_table, not from getDictionaries: which windows are
// selectable depends on the table's reading org, so they cannot be fetched once for the whole page.
//
// Windows already stored on a row are unioned in even when the org no longer offers them. The grid validates
// each cell against its dictionary, so without this, a row holding a dropped window is marked invalid and blocks saving
// unrelated rows - and if the column is hidden there is no way to repair it. The server accepts an unchanged
// stored value for the same reason, and drops it from pricing at read time. Same tactic as getCampaigns,
// which unions the table's saved campaigns so one that aged out of the index stays valid on the row using it.
export const cohortPeriodOptions = table => {
    const options = (table.cohort_periods || []).map(period => ({
        name: `${period.period_id}`,
        display_name: period.display_name,
    }));
    const known = new Set(options.map(option => option.name));

    (table.data || []).forEach(row => {
        const cell = row.cohort_period;
        if (cell && cell.name && !known.has(cell.name)) {
            known.add(cell.name);
            options.push({ name: cell.name, display_name: cell.display_name });
        }
    });

    return options;
};

export const getReferenceTable = tableName =>
    fetchValue('/reference/api/get_table', { method: 'POST', params: { table_name: tableName } });

export const updateReferenceTable = (tableName, tableData) =>
    fetchValue('/reference/api/update_table', {
        method: 'POST',
        body: { table_name: tableName, table_data: JSON.stringify(tableData) },
    });

// Returns the raw envelope so the caller can read the structured success/error payload in `value`.
export const uploadReferenceTableCsv = (file, tableName) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('table_name', tableName);
    return api.getJson('/reference/api/upload', { method: 'POST', body: formData, credentials: 'include' });
};

// The dictionaries that back the autocomplete columns. Each endpoint returns `{status, value: [...]}`.
const getApps = () => fetchValue('/api/users/self/apps');
const getOperatingSystems = () => fetchValue('/api/os');
const getAccounts = () => fetchValue('/api/account');
const getDevices = () => fetchValue('/api/devices');
const getAdNetworks = () => fetchValue('/api/users/self/adns');
const getCurrencies = () => fetchValue('/api/currencies');
const getCountries = () => fetchValue('/api/countries');

// The per-row dictionaries: fetched per grid row, scoped to the dimensions chosen on it.
export const getEvents = appId => fetchValue('/reference/api/events', { params: { app_id: appId } });

// `table_name` unions in the table's own saved campaigns, so one that aged out of the index stays
// selectable - and stays valid - on the row using it.
export const getCampaigns = ({ appId, source, query, tableName }) =>
    fetchValue('/reference/api/campaigns', {
        params: {
            limit: CAMPAIGNS_LIMIT,
            ...(appId ? { app_id: appId } : {}),
            ...(source ? { source } : {}),
            ...(query ? { q: query } : {}),
            ...(tableName ? { table_name: tableName } : {}),
        },
    });

// Fetch every dictionary in parallel and shape them into the per-field map the grid validates
// against. Ad networks feed both the source and sub-ad-network columns; currencies feed the two
// original-currency columns and the optional value-currency column.
export const getDictionaries = () =>
    Promise.all([
        getApps(),
        getOperatingSystems(),
        getAccounts(),
        getDevices(),
        getAdNetworks(),
        getCurrencies(),
        getCountries(),
    ]).then(([apps, operatingSystems, accounts, devices, adNetworks, currencies, countries]) => {
        const adNetworkOptions = adNetworks.map(adn => ({ name: adn.name, display_name: adn.display_name }));
        return {
            app: apps,
            os: operatingSystems,
            device: devices,
            source: adNetworkOptions,
            sub_adnetwork: adNetworkOptions,
            country: countries,
            adn_account_id: accounts,
            adn_original_currency: currencies,
            tracker_original_currency: currencies,
            value_currency: currencies,
        };
    });
